/* eslint-disable react/no-array-index-key */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useMemo, useState } from "react";
import { Card } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { Row } from "react-table";
import { useStateMachine } from "little-state-machine";

import "./Shipment.scss";
import usePermissions from "hooks/usePermissions";
import { getDateParams } from "helpers/commonHelpers";
import Filter from "components/organisms/Filter/Filter";
import PillTabs from "components/atoms/PillTabs/PillTabs";
import useTableQueryRequest from "hooks/useTableQueryRequest";
import { updateShipmentIdAndTitle } from "store/store";
import DataTable from "components/organisms/DataTable/DataTable";
import ListViewTour from "components/tours/listView/listViewTour";
import useSortedPaymentLocations from "hooks/useSortedPaymentLocations";
import ColumnFilter from "components/organisms/DataTable/components/ColumnFilter";
import ManageAccessDialog from "components/molecules/TopNav/common/ManageAccess/ManageAccessDialog";
import GlobalTableSearch from "components/organisms/DataTable/components/GlobalTableSearch";
import { useGetShipmentList } from "api/queries/shipments/useGetShipmentList";
import getShipmentListingColumns from "./ShipmentColumns";
import { ShipmentAdditionalColumns } from "../../../constants";
import ExportTable from "./ExportTable";

function ShipmentsV1() {
  const intl = useIntl();
  const { permissions, getPaymentLocationLabel } = usePermissions();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const paymentLocations = useSortedPaymentLocations();
  const { actions }: any = useStateMachine({ updateShipmentIdAndTitle });
  const status: string = searchParams.get("display_status") || "";

  const [selectedShipmentId, setSelectedShipmentId] = useState<string>("");
  const [showGuestAccessDialog, setShowGuestAccessDialog] = useState<boolean>(false);

  const SHIPMENT_DISPLAY_STATUS = useMemo(
    () => [
      {
        label: intl.formatMessage({ id: "general.inTransit", defaultMessage: "In transit" }),
        value: "In transit",
      },
      {
        label: intl.formatMessage({ id: "general.incomplete", defaultMessage: "Incomplete" }),
        value: "Incomplete",
      },
      {
        label: intl.formatMessage({ id: "general.delayed", defaultMessage: "Delayed" }),
        value: "Delayed",
      },
      {
        label: intl.formatMessage({ id: "general.archived", defaultMessage: "Archive" }),
        value: "archived",
      },
      {
        label: intl.formatMessage({ id: "general.delivered", defaultMessage: "Delivered" }),
        value: "Delivered",
      },
      {
        label: intl.formatMessage({ id: "general.booked", defaultMessage: "Booked" }),
        value: "Booked",
      },
    ],
    [intl],
  );

  const handleOpenGuestAccessDialog = (shipmentId: string) => {
    setSelectedShipmentId(shipmentId);
    setShowGuestAccessDialog(true);
  };

  const columns = useMemo(
    () =>
      getShipmentListingColumns(
        intl,
        JSON.parse(permissions || "{}").permissions?.quoting?.create_quote_requests,
        handleOpenGuestAccessDialog,
        null,
        true,
        JSON.parse(permissions || "{}")?.company_shipper_settings?.can_create_multidrop_quote_requests ?? false,
      ),
    [],
  );

  useEffect(() => {
    if (!JSON.parse(permissions || "{}").permissions.tracking.view_shipments) navigate("/access-denied");
  }, []);

  const getHiddenColumns = () => {
    const hiddenColumns = localStorage.getItem("shipments_hidden_columns");
    if (hiddenColumns) {
      return JSON.parse(hiddenColumns);
    }
    localStorage.setItem("shipments_hidden_columns", JSON.stringify(ShipmentAdditionalColumns));
    return ShipmentAdditionalColumns;
  };

  const { instance, loading, totalData } = useTableQueryRequest(useGetShipmentList, columns, {
    hiddenColumns: getHiddenColumns(),
    params: (tableInstance: any) => ({
      display_status: status,
      properties: ShipmentAdditionalColumns.join(","),
      ...(tableInstance?.state?.sortBy?.[0]?.id === "location" && {
        sort_by: `payment_location:${tableInstance?.state?.sortBy?.[0]?.desc ? "DESC" : "ASC"}`,
      }),
    }),
    filters: (globalFilter) => ({
      ...(globalFilter.query?.length && { query: globalFilter.query }),
      ...(globalFilter.display_status?.length && { display_status: globalFilter.display_status.join(",") }),
      ...(globalFilter.payment_location?.length && { payment_location: globalFilter.payment_location.join(",") }),
      ...(globalFilter.freight_mode?.length && { freight_modes: globalFilter.freight_mode.join(",") }),
      ...(globalFilter.requested_pickup_date?.type &&
        getDateParams(globalFilter.requested_pickup_date, "requested_pickup_date")),
      ...(globalFilter.quoted_pickup_date?.type && getDateParams(globalFilter.quoted_pickup_date, "quoted_pickup_date")),
      ...(globalFilter.expected_pickup_date?.type &&
        getDateParams(globalFilter.expected_pickup_date, "expected_pickup_date")),
      ...(globalFilter.pickup_city?.length && { pickup_city: globalFilter.pickup_city }),
      ...(globalFilter.delivery_city?.length && { delivery_city: globalFilter.delivery_city }),
      ...(globalFilter.pol_locode?.length && { pol_locode: globalFilter.pol_locode }),
      ...(globalFilter.pod_locode?.length && { pod_locode: globalFilter.pod_locode }),
      ...(globalFilter.requested_delivery_date?.type &&
        getDateParams(globalFilter.requested_delivery_date, "requested_delivery_date")),
      ...(globalFilter.quoted_delivery_date?.type &&
        getDateParams(globalFilter.quoted_delivery_date, "quoted_delivery_date")),
      ...(globalFilter.expected_delivery_date?.type &&
        getDateParams(globalFilter.expected_delivery_date, "expected_delivery_date")),
      ...(globalFilter.created_by?.length && { created_by: globalFilter.created_by }),
    }),
  });

  useEffect(() => {
    if (instance.state.globalFilter.display_status?.length) {
      searchParams.delete("display_status");
      setSearchParams(searchParams);
    }
  }, [instance.state.globalFilter.display_status]);

  const handleRowClick = async (row: Row<any>, e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
    const reference = row.original?.shipment_reference;
    const title = row.original?.shipment_title;
    actions.updateShipmentIdAndTitle({
      shipmentId: reference,
      shipmentTitle: title,
    });

    if (e.ctrlKey || e.metaKey) window.open(`/shipment/tracking/${reference}`, "_blank");
    else navigate(`/shipment/tracking/${reference}`, { state: { data: row.original?.created_by } });
  };

  const handleStatusChange = (eventKey: string | null) => {
    if (!eventKey) {
      searchParams.delete("display_status");
    } else {
      searchParams.set("display_status", eventKey || "");
    }
    setSearchParams(searchParams);
    instance.setGlobalFilter((prev: any) => ({
      ...prev,
      display_status: [],
    }));
  };

  return (
    <>
      <ListViewTour />
      <ManageAccessDialog
        shipmentId={selectedShipmentId}
        isOpen={showGuestAccessDialog}
        close={() => {
          setShowGuestAccessDialog(false);
        }}
      />
      <Card body className="shadow-none shipment-container">
        <div className="d-flex align-items-center mb-4">
          <h3 className="mr-3">Shipments</h3>
          <GlobalTableSearch
            currentFilters={instance.state.globalFilter}
            setFilters={instance.setGlobalFilter}
            filterKey="query"
            eventContext="Shipment List"
          />
        </div>
        <DataTable
          tableInstance={instance}
          totalData={totalData}
          isLoading={loading}
          onRowClick={handleRowClick}
          headerLeft={
            <PillTabs
              id="shipment-statuses"
              activeKey={status}
              onSelect={handleStatusChange}
              tabItems={[
                { eventKey: "", title: intl.formatMessage({ id: "tabs.all", defaultMessage: "All" }) },
                { eventKey: "Booked", title: intl.formatMessage({ id: "tabs.booked", defaultMessage: "Booked" }) },
                {
                  eventKey: "In transit",
                  title: intl.formatMessage({ id: "tabs.shipping", defaultMessage: "Shipping" }),
                },
                { eventKey: "Delayed", title: intl.formatMessage({ id: "tabs.delayed", defaultMessage: "Delayed" }) },
                { eventKey: "Delivered", title: intl.formatMessage({ id: "tabs.delivered", defaultMessage: "Delivered" }) },
                {
                  eventKey: "Incomplete",
                  title: intl.formatMessage({ id: "tabs.incomplete", defaultMessage: "Incomplete" }),
                },
                { eventKey: "archived", title: intl.formatMessage({ id: "tabs.archived", defaultMessage: "Archived" }) },
              ]}
            />
          }
          headerRight={
            <div className="d-flex align-items-center">
              <Filter
                presetType="shipment"
                eventContext="Shipment List"
                currentFilters={instance.state.globalFilter}
                setFilters={instance.setGlobalFilter}
                config={[
                  {
                    filterType: "multiselect",
                    label: intl.formatMessage({ id: "general.displayStatus", defaultMessage: "Display status" }),
                    key: "display_status",
                    options: SHIPMENT_DISPLAY_STATUS,
                  },
                  {
                    filterType: "multiselect",
                    label: getPaymentLocationLabel(),
                    key: "payment_location",
                    isSearchable: true,
                    options: paymentLocations,
                  },
                  {
                    filterType: "freight_mode",
                    label: intl.formatMessage({ id: "general.freightMode", defaultMessage: "Freight mode" }),
                    key: "freight_mode",
                  },
                  {
                    filterType: "accordion",
                    label: "Origin",
                    key: "origin",
                    nestedFilters: [
                      {
                        filterType: "date",
                        label: intl.formatMessage({
                          id: "general.requestedPickupDate",
                          defaultMessage: "Request Pickup date",
                        }),
                        key: "requested_pickup_date",
                      },
                      {
                        filterType: "date",
                        label: intl.formatMessage({ id: "general.quotedPickupDate", defaultMessage: "Quoted Pickup date" }),
                        key: "quoted_pickup_date",
                      },
                      {
                        filterType: "date",
                        label: intl.formatMessage({
                          id: "general.expectedPickupDate",
                          defaultMessage: "Expected Pickup date",
                        }),
                        key: "expected_pickup_date",
                      },
                      {
                        filterType: "text",
                        label: intl.formatMessage({ id: "general.pickupCity", defaultMessage: "Pickup city" }),
                        key: "pickup_city",
                      },
                      {
                        filterType: "text",
                        label: "(Air)port of Loading",
                        key: "pol_locode",
                      },
                    ],
                  },
                  {
                    filterType: "accordion",
                    label: "Destination",
                    key: "destination",
                    nestedFilters: [
                      {
                        filterType: "date",
                        label: intl.formatMessage({
                          id: "general.requestedDeliveryDate",
                          defaultMessage: "Requested Delivery date",
                        }),
                        key: "requested_delivery_date",
                      },
                      {
                        filterType: "date",
                        label: intl.formatMessage({
                          id: "general.quotedDeliveryDate",
                          defaultMessage: "Quoted Delivery date",
                        }),
                        key: "quoted_delivery_date",
                      },
                      {
                        filterType: "date",
                        label: intl.formatMessage({
                          id: "general.expectedDeliveryDate",
                          defaultMessage: "Expected Delivery date",
                        }),
                        key: "expected_delivery_date",
                      },
                      {
                        filterType: "text",
                        label: intl.formatMessage({ id: "general.deliveryCity", defaultMessage: "Delivery city" }),
                        key: "delivery_city",
                      },
                      {
                        filterType: "text",
                        label: "(Air)port of Destination",
                        key: "pod_locode",
                      },
                    ],
                  },

                  {
                    filterType: "text",
                    label: intl.formatMessage({ id: "general.createdBy", defaultMessage: "Created by" }),
                    key: "created_by",
                  },
                ]}
              />
              <div className="ml-3">
                <ColumnFilter columns={instance.allColumns} eventContext="Shipment List" listViewName="shipments" />
              </div>
              <div className="ml-3">
                <ExportTable shipmentData={instance} />
              </div>
            </div>
          }
          withoutBody
        />
      </Card>
    </>
  );
}

export default ShipmentsV1;
